import type Trail from '@alltrails/core/types/Trail';
import { TrailHit, TrailSearchResult } from '@alltrails/search/types/algoliaResultTypes';
import { TrailListItemData } from '../types/types';
import getTrailLocationAndAreaName from '../../../utils/getTrailLocationAndAreaName';

export function convertTrailToTrailListItemData(trail: Trail): TrailListItemData {
  const durationMinutes = parseInt(trail.trailGeoStats.durationMinutes ?? '0', 10);
  const locationName = getTrailLocationAndAreaName(trail);

  return {
    name: trail.name,
    slug: trail.slug,
    avgRating: trail.avgRating,
    reviewCount: trail.trailCounts.reviewCount,
    difficulty: trail.defaultActivityStats.difficulty,
    length: trail.trailGeoStats.length,
    durationMinutes,
    photoId: trail.defaultPhoto?.id,
    photoHash: trail.defaultPhoto?.photoHash,
    locationName
  };
}

export function convertTrailHitToTrailListItemData(trailHit: TrailHit): TrailListItemData {
  const [photoId, photoHash] = trailHit.profile_photo_data?.split('-') || [];
  const photoIdNumber = photoId ? parseInt(photoId, 10) : undefined;
  const difficultyNumber = parseInt(trailHit.difficulty_rating || '0', 10);

  // TrailList expects "us/ohio/:slug", the API gives us "trail/us/ohio/:slug"
  const slug = trailHit.slug.replace('trail/', '');
  const name = trailHit.name.replace(' [CLOSED]', '');
  return {
    name,
    slug,
    avgRating: trailHit.avg_rating,
    reviewCount: trailHit.num_reviews,
    difficulty: difficultyNumber,
    length: trailHit.length,
    durationMinutes: trailHit.duration_minutes,
    photoId: photoIdNumber,
    photoHash
  };
}

export function convertTrailToTrailSearchResult(trail: Trail): TrailSearchResult {
  const objectID = `trail-${trail.id}`;
  const durationMinutes = parseInt(trail.trailGeoStats?.durationMinutes ?? '0', 10);

  const hasProfilePhoto = Boolean(trail.defaultPhoto?.id && trail.defaultPhoto?.photoHash);
  const profilePhotoData = hasProfilePhoto ? `${trail.defaultPhoto!.id}-${trail.defaultPhoto!.photoHash}` : undefined;

  const hasLocation = trail.location?.latitude !== undefined && trail.location?.longitude !== undefined;
  const geoloc = hasLocation ? { lat: trail.location!.latitude, lng: trail.location!.longitude } : undefined;

  // Format slug consistently with other transformations - ensure "trail/" prefix
  const formattedSlug = trail.slug ? `trail/${trail.slug.replace(/^trail\//, '')}` : '';

  return {
    id: objectID,
    objectID,
    type: 'trail' as const,
    ID: trail.id,
    name: trail.name,
    slug: formattedSlug,
    _geoloc: geoloc,
    length: trail.trailGeoStats?.length,
    difficulty_rating: trail.defaultActivityStats?.difficulty !== undefined ? String(trail.defaultActivityStats.difficulty) : undefined,
    avg_rating: trail.avgRating,
    num_reviews: trail.trailCounts?.reviewCount,
    duration_minutes: durationMinutes,
    profile_photo_data: profilePhotoData,
    estimated_time_to_complete: trail.estimated_time_to_complete ?? trail.trailGeoStats?.averageTimeToComplete,
    area_name: trail.area?.name
  };
}
